/* eslint-disable import/no-duplicates */
import React, { useState, useEffect, useRef, ReactNode } from 'react';
import ArrowDown from '@carvana/icons/ArrowDown';
import ArrowUpFilled from '@carvana/icons/ArrowUpFilled';
import IconToggle from './IconToggle';
import Option from './Option';
import {
  SelectMenuDefault,
  SelectLabel,
  SelectLabelDefault,
  SelectWrapper,
  ValueText,
  DropdownMenuDefault,
  DropdownItemDefault
} from './Select.styles';

type OptionType = {
  display?: string;
  value?: string;
};

type SelectProps = {
  /** Standard HTML id */
  id: string;
  /** Label for select menu. This is also the basis for other internally generated ids */
  label: string;
  /** Array of options for menu. The display property is what is shown visually. */
  options: OptionType[];
  /** className, needed for wrapping Select in a styled-component */
  className?: string;
  /** Becomes the data-cv-test id on the select button. If omitted, the id is used. */
  testHook?: string;
  /** Disables the select menu */
  disabled?: boolean;
  /** Callback function to fire after selection has changed. */
  onChange?: (option: OptionType) => void;
  /** Optional value to initialize menu with */
  initialValue?: string;
  /** Template function to customize option items */
  optionTemplate?: (option: OptionType, isSelected?: boolean) => ReactNode;
  /** A Component (usually Styled) to override the built-in component */
  Menu?: typeof SelectMenuDefault;
  /** A Component (usually Styled) to override the built-in component */
  MenuLabel?: typeof SelectLabelDefault;
  /** A Component (usually Styled) to override the built-in component */
  MenuIcon?: typeof IconToggle;
  /** A Component (usually Styled) to override the built-in component */
  DropdownMenu?: typeof DropdownMenuDefault;
  /** A Component (usually Styled) to override the built-in component */
  DropdownItem?: typeof DropdownItemDefault;
  /** A Component (usually Styled) to override the built-in component */
  DropdownIcon?: typeof IconToggle;
};

type timeoutType = {
  current?: number;
};

const Select = ({
  className = '',
  testHook = '',
  disabled = false,
  label,
  onChange = () => {},
  options,
  initialValue = '',
  id,
  optionTemplate = ({ display }) => <ValueText>{display}</ValueText>,
  Menu = SelectMenuDefault,
  MenuLabel = SelectLabelDefault,
  MenuIcon = IconToggle,
  DropdownMenu = DropdownMenuDefault,
  DropdownItem = DropdownItemDefault,
  DropdownIcon = IconToggle,
  ...rest
}: SelectProps) => {
  const initialIndex = options.findIndex(v => v.value === initialValue);
  const [activeDescendant, setActiveDescendent] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);

  const findTextValue = useRef('');
  const findTextTimeout: timeoutType = useRef();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClickOutside = event => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      clearTimeout(findTextTimeout.current);
    }
  }, []);

  useEffect(() => {
    if (!showDropdown) return setActiveDescendent('');
    const getId = index => `option-${index}-${id}`;

    return setActiveDescendent(getId(selectedIndex));
  }, [showDropdown, selectedIndex]);

  const toggleDropdown = event => {
    setShowDropdown(!showDropdown);
    if (buttonRef.current && !showDropdown) {
      // workaround for Safari/Firefox focus: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#Clicking_and_focus
      buttonRef.current.focus();
    }
    event.preventDefault();
  };

  const handleOptionSelection = (event, index) => {
    const selection = options[index];
    setSelectedIndex(index);
    toggleDropdown(event);
    onChange(selection);
  };

  const locateItemByContent = key => {
    findTextValue.current += key;
    clearTimeout(findTextTimeout.current);
    const matchingIndex = options.findIndex(x =>
      x.display?.toLowerCase().startsWith(
        findTextValue.current.toLowerCase()
      )
    );

    if (matchingIndex >= 0) {
      setSelectedIndex(matchingIndex);
      onChange(options[matchingIndex]);
    }

    findTextTimeout.current = setTimeout(() => {
      findTextValue.current = '';
    }, 500) as any;
  }

  const keyboardEvents = event => {
    const { key } = event;

    switch (key) {
      case 'Escape':
      case 'Tab':
        setShowDropdown(false);
        break;
      case 'ArrowDown': {
        if (!showDropdown) toggleDropdown(event);

        if (selectedIndex < options.length - 1) {
          setSelectedIndex(selectedIndex + 1);
        }
        break;
      }
      case 'ArrowUp': {
        if (!showDropdown) toggleDropdown(event);

        if (selectedIndex > 0) {
          setSelectedIndex(selectedIndex - 1);
        }
        break;
      }
      case 'Enter':
        handleOptionSelection(event, selectedIndex);
        event.target.blur();
        break;
      default:
        locateItemByContent(key);
        break;
    }
  };

  const selection = options[selectedIndex] || {};
  const optionDisplay = optionTemplate(selection, true);
  const labelId = `label-${id}`;
  const dropdownId = `dropdown-${id}`;

  return (
    <SelectWrapper
      className={className}
      role="combobox"
      aria-haspopup="listbox"
      aria-owns={id}
      aria-expanded={showDropdown}
      ref={dropdownRef}
      aria-labelledby={labelId}
      {...rest}
    >
      <Menu
        data-cv-test={testHook || id}
        disabled={disabled}
        onClick={toggleDropdown}
        onKeyDown={keyboardEvents}
        tabIndex={0}
        ref={buttonRef}
        id={id}
        aria-controls={dropdownId}
        aria-activedescendant={activeDescendant}
        aria-labelledby={labelId}
        role="textbox"
      >
        <MenuLabel>
          <SelectLabel
            hasValue={!!selection.value}
            disabled={disabled}
            id={labelId}
          >
            {label}
          </SelectLabel>
          {optionDisplay}
        </MenuLabel>
        <MenuIcon
          condition={showDropdown}
          ifTrue={ArrowUpFilled}
          ifFalse={ArrowDown}
        />
      </Menu>

      <DropdownMenu
        role="listbox"
        aria-labelledby={labelId}
        id={dropdownId}
      >
        {showDropdown && options.map((option, index) => (
          <Option
            option={option}
            DropdownIcon={DropdownIcon}
            DropdownItem={DropdownItem}
            template={optionTemplate}
            id={id}
            index={index}
            isSelected={index === selectedIndex}
            handleOptionSelection={handleOptionSelection}
            key={`${option.value}_option_${id}`}
          />
        ))}
      </DropdownMenu>
    </SelectWrapper>
  );
};

export default Select;
